/**
 * ChangeRelationshipBasis — staff control to switch a partner matter between
 * De facto and Married mid-application (Tzvi, 2026-07-20: couples genuinely
 * marry between engagement and lodgement; the matter used to be stuck on the
 * basis picked at creation, with the form answer fixable but the matter label
 * and the de facto lodgement gate stale).
 *
 * What it does on apply:
 *   1. Updates matters.variant (drives the matter label, welcome copy, doc
 *      checklist fallback and the de facto legal-basis lodgement gate).
 *   2. Syncs the intake answers on the applicant's 47SP: relationship status
 *      for the applicant AND the sponsor block, editing the saved blob rows
 *      (blob wins over seed rows, so editing the blob is authoritative) and
 *      falling back to seed rows for untouched forms.
 * The client's follow-up questions (marriage date vs de facto date) follow
 * the synced answer automatically. Staff-only; partner matters only; the 300
 * PMV is excluded (an engaged couple who marry need a different visa, not a
 * variant flip).
 */
import { useState } from 'react';
import { Repeat2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Json } from '../lib/database.types';
import { statusForVariant, relationshipStatusForVariant } from '../lib/relationshipBasis';

const SWITCHABLE_SUBCLASSES = new Set(['820', '801', '309', '100']);
const VARIANTS = [
  { value: 'defacto', label: 'De facto' },
  { value: 'married', label: 'Married' },
];

interface Props {
  matterId: string;
  visaSubclass: string;
  variant: string | null;
  /** Matter-level relationship status (R5). Null means it has not been set on
   *  this matter yet, so staff are prompted to confirm it. */
  relationshipStatus: string | null;
  applicantContactId: string;
}

export function ChangeRelationshipBasis({ matterId, visaSubclass, variant, relationshipStatus, applicantContactId }: Props) {
  const [next, setNext] = useState<string>(variant ?? 'defacto');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!SWITCHABLE_SUBCLASSES.has(visaSubclass)) return null;
  // Render for defacto / married AND for a matter with no variant yet: the
  // relationship-status field is required and client-locked (R5), so a
  // null-variant partner matter would otherwise leave the client blocked on a
  // field nobody can set. Staff set it here. Other variants stay excluded.
  if (variant !== null && variant !== 'defacto' && variant !== 'married') return null;

  const dirty = next !== variant;

  const apply = async () => {
    const from = variant === null ? 'not set' : (VARIANTS.find((v) => v.value === variant)?.label ?? variant);
    const to = VARIANTS.find((v) => v.value === next)?.label ?? next;
    const ok = window.confirm(
      `Change the relationship basis from ${from} to ${to}?\n\n` +
        'This updates the matter and the relationship status answers on the intake form. ' +
        'The matching follow-up questions and document checklist switch automatically. ' +
        (next === 'married' ? 'The de facto legal-basis sign-off will no longer apply.' : 'The de facto legal-basis sign-off will now be required before lodgement.'),
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const status = statusForVariant(next);
      // Keep the matter-level relationship status (R5) in lockstep with the
      // variant, so the client's locked intake display and the appears_when
      // gates always bind to the same value staff see here.
      const { error: mErr } = await supabase
        .from('matters')
        .update({ variant: next, relationship_status: relationshipStatusForVariant(next) })
        .eq('id', matterId);
      if (mErr) throw mErr;

      const { data: forms, error: fErr } = await supabase
        .from('intake_forms')
        .select('id, form_code')
        .eq('matter_id', matterId)
        .eq('contact_id', applicantContactId);
      if (fErr) throw fErr;
      const applicantForm = (forms ?? []).find(
        (f) => f.form_code.startsWith('47SP') && !f.form_code.toLowerCase().includes('stage2'),
      );

      if (applicantForm && status) {
        const { data: rows, error: rErr } = await supabase
          .from('intake_field_data')
          .select('field_key, field_value')
          .eq('intake_form_id', applicantForm.id)
          .in('field_key', ['applicant', 'sponsor']);
        if (rErr) throw rErr;
        const byKey = new Map((rows ?? []).map((r) => [r.field_key, r.field_value]));
        const upserts: Array<{ intake_form_id: string; field_key: string; field_value: Json }> = [];
        for (const segment of ['applicant', 'sponsor']) {
          const blob = byKey.get(segment);
          if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
            const obj = blob as Record<string, Json>;
            const rel = (obj.relationship && typeof obj.relationship === 'object' && !Array.isArray(obj.relationship)
              ? (obj.relationship as Record<string, Json>)
              : {});
            upserts.push({
              intake_form_id: applicantForm.id,
              field_key: segment,
              field_value: { ...obj, relationship: { ...rel, status } } as Json,
            });
          } else {
            // untouched form: the dotted seed row is the value hydration reads
            upserts.push({
              intake_form_id: applicantForm.id,
              field_key: `${segment}.relationship.status`,
              field_value: status as Json,
            });
          }
        }
        const { error: uErr } = await supabase
          .from('intake_field_data')
          .upsert(upserts, { onConflict: 'intake_form_id,field_key' });
        if (uErr) throw uErr;
      }

      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change the relationship basis.');
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        <Repeat2 size={15} className="text-[#2CABEA]" />
        <p className="text-sm font-medium text-slate-700">Relationship basis</p>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Set at matter creation, but couples change: a de facto couple who marry before lodgement can be
        switched here. Updates the matter and the intake answers together.
      </p>
      {!relationshipStatus && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mb-3">
          Relationship status is not set on this matter yet. Confirm it below so it shows on the client's form.
        </p>
      )}
      <div className="flex items-center gap-2">
        <select className="form-input text-sm w-40" value={next} onChange={(e) => setNext(e.target.value)} disabled={busy}>
          {VARIANTS.map((v) => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </select>
        <button type="button" className="btn-secondary text-sm disabled:opacity-50" onClick={apply} disabled={!dirty || busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : 'Apply change'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}
